import { type FC, useEffect, useState } from "react"
import { router } from "expo-router"
import { View } from "react-native"
import {
  useCheckAccountExists,
  useLoginMutation,
  useRegistrationMutation,
} from "@/queries/user"
import { PrimaryDarkButton } from "@/components/buttons/CommonButtons"
import { showErrorToast } from "@/components/toasts/AppToast"
import { useRegistrationInputs } from "@/stores/register"
import * as registerEvents from "@/analytics/events/register"
import Head from "expo-router/head"
import BFText, { Body, H1, H4 } from "@/components/standard/BFText"
import useDeviceAttributes from "@/hooks/useDeviceAttributes"
import { usePreQualState } from "@/stores/preQual"
import type { UsernamePasswordLoginMutation } from "@/generated/graphql"
import { useSafeAreaInsets } from "react-native-safe-area-context"
import { OrDivider } from "@/screens/onboarding/components/OrDivider"
import { SocialLogin } from "@/screens/onboarding/components/SocialLogin"
import useBrowserInfo from "@/hooks/useBrowserInfo"
import { EmailAndPasswordForm } from "@/screens/onboarding/components/EmailAndPasswordForm"
import { SignInLink } from "@/screens/onboarding/components/SignInLink"
import { TealifyText } from "@/components/TealifyText"
import { fbqTrackCustom, fbqUserAttributes } from "@/utils/PixelUtils"
import Rocket from "@/assets/clipart/rocket.svg"
import { type SvgProps } from "react-native-svg"
import { Vertical } from "@/components/standard/BFLayout"

const ICONS = {
  rocket: Rocket,
} as any

export default function RegistrationScreen({
  title,
  body,
  subTitle,
  icon,
}: {
  title: string
  body?: string
  subTitle?: string
  icon?: string
}) {
  const insets = useSafeAreaInsets()
  const registrationMutation = useRegistrationMutation()
  const loginMutation = useLoginMutation()
  const checkAccountExists = useCheckAccountExists()
  const Icon = ICONS[icon ?? ""] as FC<SvgProps> | undefined
  const preQualState = usePreQualState()

  const { isWeb, isAndroid } = useDeviceAttributes()

  const { isEmbeddedBrowser } = useBrowserInfo()

  const hideSocialLogin = isWeb && isEmbeddedBrowser

  const {
    registerInputs,
    inputErrors,
    setEmail,
    setPassword,
    validateEmailSignUpInputs,
    setConfirmationMode,
    reset,
  } = useRegistrationInputs()

  useEffect(() => {
    reset()
    setAccountExistsError(null)
  }, [])

  const { email, password } = registerInputs

  const [accountExistsError, setAccountExistsError] = useState<string | null>(
    null
  )

  const hasPendingMutations =
    registrationMutation.isPending || checkAccountExists.isPending

  const onSignUp = () => {
    if (validateEmailSignUpInputs()) {
      checkAccountExists.mutate(
        {
          email,
        },
        {
          onSuccess: (accountExists) => {
            if (accountExists) {
              loginMutation.mutate(
                {
                  username: email,
                  password,
                },
                {
                  onSuccess: (data: UsernamePasswordLoginMutation) => {
                    if (data?.login?.token) {
                      console.log("Login successful")
                    } else {
                      showErrorToast({
                        title: "Incorrect Password",
                        message: "Click here if you forgot your password.",
                        onPress: () => {
                          router.push("/reset-password")
                        },
                      })
                    }
                  },
                  onError: () => {
                    setAccountExistsError(
                      "An account with this email already exists.\nSign In to your account to continue."
                    )
                  },
                }
              )
            } else {
              const fbqAttributes = fbqUserAttributes(preQualState, null)
              fbqAttributes.email = email
              fbqTrackCustom("UserSignup", fbqAttributes)
              setAccountExistsError(null)
              registerEvents.emailRegistrationSubmitted({ email })
              setConfirmationMode("email")
              router.push("/confirm")
            }
          },
        }
      )
    }
  }

  return (
    <>
      <Head>
        <title>Backflip - Sign Up</title>
      </Head>
      <Vertical className={"justify-between flex-1"}>
        <Vertical className={"flex-1"}>
          {Icon && (
            <View
              className={"absolute right-0 top-[-50px] opacity-25 size-[250px]"}
            >
              <Icon width={"100%"} height={"100%"} />
            </View>
          )}
          <View className={"py-4 gap-2"}>
            <H1 color={"white"}>
              <TealifyText text={title} />
            </H1>

            {body && (
              <Body color={"white"}>
                <TealifyText text={body} />
              </Body>
            )}

            {subTitle && (
              <H4 color={"white"} className={"mt-5"}>
                <TealifyText text={subTitle} />
              </H4>
            )}
          </View>

          {!hideSocialLogin && (
            <View>
              <SocialLogin loginType="register" />
              <OrDivider hide={hideSocialLogin} />
            </View>
          )}

          <EmailAndPasswordForm
            email={email}
            setEmail={setEmail}
            emailError={inputErrors.email}
            password={password}
            setPassword={setPassword}
            passwordError={inputErrors.password}
          />

          <View className="flex flex-col items-center justify-center flex-1">
            {accountExistsError && (
              <View className="w-[273px] pt-[17px] mx-auto">
                <BFText className="font-spacegrotesk text-[12px] leading-4 text-center text-salmon">
                  {accountExistsError}
                </BFText>
              </View>
            )}
          </View>
        </Vertical>

        <Vertical
          className={`w-full shrink-0 gap-3 pt-2`}
          style={{ marginBottom: insets.bottom + (isAndroid ? 60 : 0) }}
        >
          <PrimaryDarkButton
            testID="sign-up-button"
            title={"Continue Registration"}
            onPress={onSignUp}
            disabled={hasPendingMutations}
          />
          <SignInLink />
        </Vertical>
      </Vertical>
    </>
  )
}
